import type { UploadedAttachment } from '../api/files';
import type { MessageAttachment } from '../types/api';

/**
 * yaml SSOT (`MessageSendRequest.attachments` + `MailComposeRequest.attachments`) 의 maxItems
 * 와 정합. UI 차원에서 11개째 첨부 시도를 차단해 PUT + finalize 비용 낭비 차단 (Stage 3 #7
 * 봉합).
 */
export const MAX_ATTACHMENTS = 10;

/**
 * UploadedAttachment (frontend upload state) → MessageAttachment (wire shape).
 * chat MessageInput 의 toApiAttachment helper 를 lib 으로 추출 — chat / mail / board cross-domain
 * 재사용. wire SSOT 정합 (yaml MessageAttachment schema).
 */
export function toMessageAttachment(a: UploadedAttachment): MessageAttachment {
  return {
    fileId: a.fileId,
    // 모르는 값은 적지 않는다 — 워크스페이스에서 고른 파일에는 키가 없다(2026-08-22).
    ...(a.fileKey ? { fileKey: a.fileKey } : {}),
    name: a.name,
    mimeType: a.mimeType,
    sizeBytes: a.sizeBytes,
  };
}

/**
 * 바이트 → 사람 친화 포맷 (B/KB/MB/GB).
 * 2026-04-29 attachment-ssot-refactor: 3중 SSOT (lib + AttachmentList + workspace) 통합 — lib 단일 위치.
 * GB 정밀도 `.toFixed(2)` 통일 (workspace 측 legacy `.toFixed(1)` 의 intentional drift correction).
 */
export function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/**
 * unknown → MessageAttachment[] narrow cast. wire response 의 attachments 가 untyped 일 때
 * 사용. fileId pattern '^[0-9]+$' 강제 + free-form additionalProperties 허용 (yaml SSOT 정합).
 * 2026-04-29 attachment-ssot-refactor: AttachmentList internal helper 였던 것을 lib SSOT 로 이동.
 * cross-domain (chat / mail / board / calendar 등) 모두 동일 사용.
 */
export function narrowAttachments(input: unknown): MessageAttachment[] {
  if (!Array.isArray(input)) return [];
  return input.filter(isMessageAttachment);
}

export function isMessageAttachment(x: unknown): x is MessageAttachment {
  return (
    typeof x === 'object' && x !== null &&
    typeof (x as { fileId?: unknown }).fileId === 'string' &&
    /^[0-9]+$/.test((x as { fileId: string }).fileId)
  );
}

export function isImageMime(mime: string): boolean {
  return mime.startsWith('image/');
}

// chat-inline-media-playback (2026-07-12) — 오디오/비디오 첨부 인라인 재생 분기용 (additive).
export function isAudioMime(mime: string): boolean {
  return mime.startsWith('audio/');
}

export function isVideoMime(mime: string): boolean {
  return mime.startsWith('video/');
}
